import { z } from 'zod';

// Mirrors backend SignCare.Application.Questionnaires.Dtos.

export const questionnaireFieldDtoSchema = z.object({
  id: z.string().uuid(),
  fieldType: z.string(),
  label: z.string(),
  placeholder: z.string().nullable(),
  isMandatory: z.boolean(),
  sortOrder: z.number().int(),
  // Nullish, not nullable: an older API build omits these keys entirely, and a
  // strict `.nullable()` would fail the whole parse and blank the builder.
  validationMessage: z.string().nullish(),
  // Raw JSON string, parsed by the element registry — see fieldConfig.ts. Left
  // opaque here on purpose: a typed schema per element type would mean this file
  // has to change every time the palette grows.
  configJson: z.string().nullish(),
});

export const questionnaireSectionDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sortOrder: z.number().int(),
  fields: z.array(questionnaireFieldDtoSchema),
  // Applicant-facing heading / sub-heading. `name` is the builder tab label.
  title: z.string().nullish(),
  description: z.string().nullish(),
});

export const questionnaireDetailDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  sections: z.array(questionnaireSectionDtoSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type QuestionnaireDetailDto = z.infer<typeof questionnaireDetailDtoSchema>;

export const questionnaireDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  sectionCount: z.number().int(),
  fieldCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Distinct element types in journey order — drives the Features icon strip.
  // Defaulted so a build newer than the API still lists questionnaires, just
  // without icons, instead of failing the whole page parse.
  fieldTypes: z.array(z.string()).default([]),
});

export type QuestionnaireDto = z.infer<typeof questionnaireDtoSchema>;

export const pagedQuestionnairesSchema = z.object({
  items: z.array(questionnaireDtoSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});

// Save payload (PUT). null id = new row.
export interface FieldSaveInput {
  id: string | null;
  fieldType: string;
  label: string;
  placeholder: string | null;
  isMandatory: boolean;
  sortOrder: number;
  validationMessage: string | null;
  /** Serialised field config, or null when the type has none. See fieldConfig.ts. */
  configJson: string | null;
}

export interface SectionSaveInput {
  id: string | null;
  name: string;
  sortOrder: number;
  fields: FieldSaveInput[];
  title: string | null;
  description: string | null;
}

export interface SaveQuestionnaireInput {
  name: string;
  description: string | null;
  sections: SectionSaveInput[];
}
